import { useEffect, useRef, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Move focus to the main landmark after a client-side navigation (05 §6.4).
 *
 * Without this, a keyboard or screen-reader user who follows a link stays parked
 * on the old (now unmounted) element and hears nothing about the new page. The
 * first render is skipped on purpose: stealing focus on page load would fight
 * the browser's own restore behaviour and jump past the skip link.
 *
 * The target needs `tabIndex={-1}` so it can receive programmatic focus without
 * entering the tab order.
 */
export function useRouteFocus(target: RefObject<HTMLElement | null>): void {
  const { pathname } = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const element = target.current;
    if (!element) return;
    element.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname, target]);
}
